/**
 * TS-7939: 3921/3922 recipient name for IRIS A2A.
 *
 * The IRIS RecipientDetailType is an xsd:choice of PersonName or BusinessName
 * with TINSubmittedTypeCd INDIVIDUAL_TIN / BUSINESS_TIN. A 3921/3922 recipient
 * can be an EIN-holding entity (an estate or trust exercising after the
 * employee's death); the legacy TSC1099 2026 layout stores those in
 * (BOX_)EMPLOYEE_BUSINESS_NAME1/2 with EMPLOYEE_ID_TYPE = '1'. The generators
 * previously hardcoded INDIVIDUAL_TIN + PersonName, which produced wrong XML
 * for such rows.
 */

export interface EmployeeRecipientNameSource {
  EMPLOYEE_ID_TYPE?: string | null;
  EMPLOYEE_NAME?: string | null;
  EMPLOYEE_NAME2?: string | null;
  EMPLOYEE_FIRST_NAME?: string | null;
  EMPLOYEE_MIDDLE_NAME?: string | null;
  EMPLOYEE_LAST_NAME?: string | null;
  EMPLOYEE_SUFFIX?: string | null;
  EMPLOYEE_BUSINESS_NAME1?: string | null;
  EMPLOYEE_BUSINESS_NAME2?: string | null;
  BOX_EMPLOYEE_BUSINESS_NAME1?: string | null;
  BOX_EMPLOYEE_BUSINESS_NAME2?: string | null;
}

export interface EmployeeRecipientNameGroup {
  TINSubmittedTypeCd: "INDIVIDUAL_TIN" | "BUSINESS_TIN";
  PersonNameControlTxt?: string;
  PersonName?: { PersonFirstNm: string; PersonLastNm?: string };
  BusinessNameControlTxt?: string;
  BusinessName?: {
    BusinessNameLine1Txt: string;
    BusinessNameLine2Txt?: string;
  };
}

const clean = (v: string | null | undefined): string => (v ?? "").trim();

/** IRS name control: first 4 significant characters, uppercased. */
const nameControl = (name: string): string =>
  name.replace(/[^A-Za-z0-9&-]/g, "").substring(0, 4).toUpperCase();

export function buildEmployeeRecipientName(
  record: EmployeeRecipientNameSource,
): EmployeeRecipientNameGroup {
  const businessName1 =
    clean(record.EMPLOYEE_BUSINESS_NAME1) ||
    clean(record.BOX_EMPLOYEE_BUSINESS_NAME1);
  const businessName2 =
    clean(record.EMPLOYEE_BUSINESS_NAME2) ||
    clean(record.BOX_EMPLOYEE_BUSINESS_NAME2);
  const isBusiness =
    clean(record.EMPLOYEE_ID_TYPE) === "1" || businessName1 !== "";

  if (isBusiness) {
    // ID_TYPE said business but no dedicated column was filled: the legacy
    // single name line is the business name.
    const line1 = businessName1 || clean(record.EMPLOYEE_NAME) || "UNKNOWN";
    const ctrl = nameControl(line1);
    return {
      TINSubmittedTypeCd: "BUSINESS_TIN",
      ...(ctrl && { BusinessNameControlTxt: ctrl }),
      BusinessName: {
        BusinessNameLine1Txt: line1,
        ...(businessName2 && { BusinessNameLine2Txt: businessName2 }),
      },
    };
  }

  // Person: prefer the 2026 split columns, fall back to the legacy name pair.
  const first =
    clean(record.EMPLOYEE_FIRST_NAME) || clean(record.EMPLOYEE_NAME) || "UNKNOWN";
  const last =
    clean(record.EMPLOYEE_LAST_NAME) || clean(record.EMPLOYEE_NAME2) || "UNKNOWN";
  const ctrl = nameControl(last !== "UNKNOWN" ? last : first);
  return {
    TINSubmittedTypeCd: "INDIVIDUAL_TIN",
    ...(ctrl && { PersonNameControlTxt: ctrl }),
    PersonName: {
      PersonFirstNm: first,
      ...(last && { PersonLastNm: last }),
    },
  };
}
